import React, { useEffect } from 'react';
import Modal from 'ds/components/modal';
import { useForm } from 'react-final-form-hooks';
import { ReportForm } from './ReportUploadBulletinForm';
import {
  ReportPopupContentWrapper,
  WhatsAppButtonWrapper,
  WhatsAppButtonLabel,
  Divider,
  ReportAddressRoot,
} from './styled';
import { Button } from 'ds/components/button';
import { useLocale } from 'locale';
import WhatsAppIcon from 'assets/svg/whatsapp.svg';
import { createWhatsAppLink } from 'utils/shareLinksUtils';
import { useScreenBreakpoint } from 'consts/breakpoints';
import { ReportFormFields, ReportPopupViewProps, IReportAddressData } from './interfaces';
import withEvents from 'analytics/withEvents';
import { noop } from 'lodash';
import { DealType } from 'utils/entities';
import defaultTheme from 'ds/theme/default';
import { pageSourceToAnalytics } from 'components/upload-bulletin/report-error-popup/helpers';
import { isEmailValid } from 'utils/formValidationHelpers';


export const SUPPORT_WHATSAPP_NUMBER = '+972778041418';
const MIN_ADDRESS_NAME_LENGTH = 5;

const ReportPopupView: React.FC<ReportPopupViewProps> = ({
  email,
  modalState,
  onClosePopup,
  onSubmit,
  onPopupOpen = noop,
  onWhatsAppClick = noop,
}) => {
  const { t } = useLocale();
  const isMobile = useScreenBreakpoint() < 3;
  const footerStyle = isMobile ? { padding: defaultTheme.spacing(3), borderTop: 'none' } : undefined;
  const headerStyle = isMobile ? { boxShadow: 'none' } : undefined;
  const { isOpen, docId, reportSource, dealType, id } = modalState || {};

  const initialValues = {
    addressName: docId,
    commentMessage: '',
    userEmail: email,
  };

  const onSubmitForm = (values: any) => {
    const submitPopupDataObj: IReportAddressData = {
      message: values.commentMessage,
      email: values.userEmail,
      docId: values.addressName,
      reportSource,
      dealType,
      id,
    };

    onSubmit(submitPopupDataObj);
    onClose();
  };

  const { form, valid } = useForm<ReportFormFields>({
    onSubmit: onSubmitForm,
    initialValues,
    validateOnBlur: true,
    validate: (values: ReportFormFields) => {
      const errors: Partial<ReportFormFields> = {};

      if (!values.userEmail) {
        errors.userEmail = t('reportAddress.popup.email.required');
      }
      else if (!isEmailValid(values.userEmail)) {
        errors.userEmail = t('reportAddress.popup.email.invalid');
      }

      if (!values.addressName) {
        errors.addressName = t('reportAddress.popup.addressName.required');
      }
      else if (values.addressName.trim().length < MIN_ADDRESS_NAME_LENGTH) {
        errors.addressName = t('reportAddress.popup.addressName.invalid');
      }

      return errors;
    },
  });

  useEffect(() => {
    if (isOpen) onPopupOpen();
  }, [ isOpen ]);

  if (!modalState || !modalState.isOpen) return null;

  const onClose = () => {
    onClosePopup();
    setTimeout(() => form.reset());
  };

  const whatsAppClick = () => {
    onWhatsAppClick();
    const urlObj = new URL(window.location.href);
    urlObj.search = '';
    const href = createWhatsAppLink({ phone: SUPPORT_WHATSAPP_NUMBER });
    window.open(href);
  };

  return (
    <Modal
      title={t('reportAddress.popup.title')}
      isOpen={isOpen}
      onClose={onClose}
      headerStyle={headerStyle}
      footerStyle={footerStyle}
      isFullWidthMainContent
      buttons={(
        <Button
          data-auto="report-submit-button"
          disabled={!valid}
          onClick={form.submit}
          fullWidth
          size="extraLarge"
        >
          {t('reportListing.popup.button.text')}
        </Button>
      )}
    >
      <ReportAddressRoot>
        <WhatsAppButtonWrapper>
          <Button size="extraLarge" mode="ghost" fullWidth onClick={whatsAppClick} isInitialIconColor>
            <WhatsAppIcon width={24} height={24} />
            <WhatsAppButtonLabel weight="medium">{t('reportAddress.popup.whatsapp')}</WhatsAppButtonLabel>
          </Button>
        </WhatsAppButtonWrapper>
        <Divider />
        <ReportPopupContentWrapper>
          <ReportForm formInstance={form} />
        </ReportPopupContentWrapper>
      </ReportAddressRoot>
    </Modal>
  );
};

export default withEvents<ReportPopupViewProps>((sendEvent, props) => {
  if (!props.modalState) return null;

  const dealType = props.modalState.dealType;
  const id = props.modalState.id;

  const poiPayload = {
    property: {
      deal_type: dealType === DealType.Buy ? 'for_sale' : 'for_rent',
      property_id: id || 'newListing',
    },
  };
  const event = {
    source: pageSourceToAnalytics[props.routeParams.source] || 'other',
    report_source: props.modalState.reportSource,
  };

  return {
    onPopupOpen() {
      sendEvent('bulletin_manage_error_report_form_open', 'bulletin_manage', {
        ...poiPayload,
        event,
      });
    },
    onWhatsAppClick() {
      sendEvent('bulletin_manage_whatsapp_click', 'bulletin_manage', {
        ...poiPayload,
        event,
      });
    },
  };
})(ReportPopupView);
